import React, { useState, useEffect } from "react";
import { Line, Doughnut } from "react-chartjs-2";
import "chart.js/auto";
import { motion, AnimatePresence } from "framer-motion";
import { useDispatch } from "react-redux";
import {
  Users,
  ShoppingCart,
  Wallet,
  MessageSquare,
  Settings,
  Star,
  User,
  Bell,
  PlusCircle,
  Grid,
  X,
  TrendingUp,
  TrendingDown,
  DollarSign,
  Package,
  AlertCircle,
  CheckCircle,
  Clock,
  ArrowRight,
  Sparkles,
  Activity,
  Zap,
} from "lucide-react";
import { UsersPanelContent } from "./panells/UsersPanelContent";
import { OrdersPanelContent } from "./panells/OrdersPanelContent";
import { TransactionsPanelContent } from "./panells/TransactionsPanelContent";
import { MessagesPanelContent } from "./panells/MessagesPanelContent";
import { ReviewsPanelContent } from "./panells/ReviewsPanelContent";
import { SettingsPanelContent } from "./panells/SettingsPanelContent";
import { AccountPanelContent } from "./panells/AccountPanelContent";
import { HubConnectionBuilder, LogLevel } from "@microsoft/signalr";
import axios from "axios";
import { PROFILE_API, PAYMENT_API, ORDER_API } from "../data/api";
import { useNavigate } from "react-router-dom";
import { clearOrders } from "../redux/ordersSlice";
import { clearCart } from "../redux/cartSlice";

// Sidebar Item Component
const SidebarItem = ({ icon, label, active, onClick, badge }) => (
  <motion.button
    onClick={onClick}
    whileHover={{ scale: 1.02, x: 4 }}
    whileTap={{ scale: 0.98 }}
    className={`flex items-center gap-3 w-full px-4 py-3 rounded-xl transition-all ${
      active
        ? "bg-gradient-to-r from-indigo-600 to-purple-600 text-white shadow-lg shadow-indigo-500/30"
        : "text-gray-600 hover:bg-gray-100 hover:text-gray-900"
    }`}
  >
    <span className="w-6 h-6">{icon}</span>
    <span className="text-sm font-medium flex-1 text-left">{label}</span>
    {badge && (
      <span className="px-2 py-0.5 bg-red-500 text-white text-xs rounded-full">
        {badge}
      </span>
    )}
  </motion.button>
);

// Stat Card Component
const StatCard = ({ title, value, change, icon: Icon, gradient, chartColor }) => (
  <motion.div
    whileHover={{ scale: 1.02, y: -4 }}
    className="bg-white rounded-2xl p-6 shadow-lg border border-gray-100 overflow-hidden relative"
  >
    <div className={`absolute top-0 right-0 w-32 h-32 bg-gradient-to-br ${gradient} opacity-10 rounded-full -translate-y-1/2 translate-x-1/2`} />
    
    <div className="relative">
      <div className="flex items-center justify-between mb-4">
        <div className={`w-12 h-12 rounded-xl bg-gradient-to-br ${gradient} flex items-center justify-center shadow-lg`}>
          <Icon className="w-6 h-6 text-white" />
        </div>
        <div className={`flex items-center gap-1 text-sm font-medium ${
          change >= 0 ? "text-green-500" : "text-red-500"
        }`}>
          {change >= 0 ? <TrendingUp size={16} /> : <TrendingDown size={16} />}
          {Math.abs(change)}%
        </div>
      </div>
      
      <p className="text-gray-500 text-sm mb-1">{title}</p>
      <h3 className="text-3xl font-bold text-gray-800">{value}</h3>
      
      <div className="mt-4 h-8">
        <svg viewBox="0 0 100 20" className="w-full h-full">
          <path
            d="M0,15 Q10,12 20,14 T40,10 T60,12 T80,8 T100,10"
            fill="none"
            stroke={chartColor}
            strokeWidth="2"
            strokeLinecap="round"
          />
        </svg>
      </div>
    </div>
  </motion.div>
);

// Notification Item
const NotificationItem = ({ notification, onDismiss }) => (
  <motion.div
    initial={{ opacity: 0, x: 20 }}
    animate={{ opacity: 1, x: 0 }}
    exit={{ opacity: 0, x: -20 }}
    className="flex items-start gap-3 p-4 hover:bg-gray-50 border-b border-gray-100 transition-colors"
  >
    <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
      notification.type === "lead" ? "bg-green-100 text-green-600" :
      notification.type === "message" ? "bg-blue-100 text-blue-600" :
      "bg-purple-100 text-purple-600"
    }`}>
      {notification.type === "lead" ? <Zap size={18} /> :
       notification.type === "message" ? <MessageSquare size={18} /> :
       <Activity size={18} />}
    </div>
    <div className="flex-1">
      <p className="text-sm font-medium text-gray-800">{notification.message}</p>
      <p className="text-xs text-gray-400 mt-1">{notification.time}</p>
    </div>
    <button 
      onClick={() => onDismiss(notification.id)}
      className="text-gray-400 hover:text-gray-600"
    >
      <X size={16} />
    </button>
  </motion.div>
);

const AdminDashboard = () => {
  const dispatch = useDispatch();
  const [activeTab, setActiveTab] = useState("Dashboard");
  const [openPanel, setOpenPanel] = useState(null);
  const [notifications, setNotifications] = useState([]);
  const [showNotifications, setShowNotifications] = useState(false);
  const [dashboardStats, setDashboardStats] = useState({
    orders: 0,
    completedOrders: 0,
    revenue: 0,
    users: 0,
    active: 0,
    pendingOrders: 0,
    totalProducts: 0,
  });
  const navigate = useNavigate();

  useEffect(() => {
    const loadDashboardStats = async () => {
      try {
        const [ordersRes, paymentsRes, usersRes] = await Promise.all([
          axios.get(ORDER_API.GET_ALL),
          axios.get(PAYMENT_API.GET_ALL),
          axios.get(PROFILE_API.GET_ALL),
        ]);

        const orders = ordersRes.data.data || ordersRes.data;
        const payments = paymentsRes.data.data || paymentsRes.data;
        const users = usersRes.data.data || usersRes.data;
        const activeUsers = users.filter(
          (u) => (u.type === "Customer" || u.type === "Vendor") && u.status === "Active"
        ).length;

        setDashboardStats({
          orders: orders.length,
          completedOrders: orders.filter((o) => o.orderStatus === "Completed").length,
          pendingOrders: orders.filter((o) => o.orderStatus === "Pending").length,
          revenue: payments
            .filter((p) => p.paymentStatus === "Completed")
            .reduce((s, p) => s + (p.paymentAmount || 0), 0),
          users: users.length,
          active: activeUsers,
          totalProducts: 156,
        });
      } catch (err) {
        console.error("Dashboard stats error", err);
      }
    };

    loadDashboardStats();
  }, []);

  // SignalR Connection
  useEffect(() => {
    const connection = new HubConnectionBuilder()
      .withUrl("https://localhost:44370/notificationHub?role=Admin")
      .withAutomaticReconnect()
      .configureLogging(LogLevel.Information)
      .build();

    connection
      .start()
      .then(() => console.log("SignalR connected"))
      .catch((err) => console.error("SignalR connection error:", err));

    connection.on("ReceiveNotification", (message) => {
      const newNotification = {
        id: Date.now(),
        message,
        type: "activity",
        time: new Date().toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }),
      };
      setNotifications((prev) => [newNotification, ...prev]);
    });

    return () => connection.stop();
  }, []);

  const tabs = [
    { name: "Dashboard", icon: <Grid size={18} /> },
    { name: "Users", icon: <Users size={18} />, panel: "users" },
    { name: "Orders", icon: <ShoppingCart size={18} />, panel: "orders", badge: dashboardStats.pendingOrders },
    { name: "Transactions", icon: <Wallet size={18} />, panel: "transactions" },
    { name: "Reviews", icon: <Star size={18} />, panel: "reviews" },
    { name: "Verifications", icon: <Settings size={18} />, panel: "settings" },
    { name: "Account", icon: <User size={18} />, panel: "account" },
  ];

  const revenueLineData = {
    labels: ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug"],
    datasets: [
      {
        label: "2024",
        data: [100, 80, 95, 70, 60, 50, 20, 40],
        borderColor: "#6366f1",
        backgroundColor: "rgba(99, 102, 241, 0.08)",
        tension: 0.4,
        fill: true,
      },
      {
        label: "2023",
        data: [40, 60, 80, 110, 130, 90, 120, 140],
        borderColor: "#ec4899",
        backgroundColor: "rgba(236, 72, 153, 0.06)",
        tension: 0.4,
        fill: true,
      },
    ],
  };

  const orderStatusData = {
    labels: ["Completed", "Pending", "Cancelled"],
    datasets: [
      {
        data: [dashboardStats.completedOrders, dashboardStats.pendingOrders, 15],
        backgroundColor: ["#10b981", "#f59e0b", "#ef4444"],
        borderWidth: 0,
      },
    ],
  };

  const revenueOptions = {
    plugins: { legend: { display: false } },
    animation: { duration: 600 },
    maintainAspectRatio: false,
    scales: {
      x: { grid: { display: false } },
      y: { grid: { color: "#f3f4f6" }, ticks: { stepSize: 20 } },
    },
  };

  const doughnutOptions = {
    plugins: {
      legend: {
        position: "bottom",
        labels: { usePointStyle: true, padding: 16 },
      },
    },
    cutout: "70%",
    animation: { duration: 600 },
  };

  const openPanelHandler = (panelKey) => setOpenPanel(panelKey);
  const closePanel = () => setOpenPanel(null);
  const dismissNotification = (id) => {
    setNotifications((prev) => prev.filter((n) => n.id !== id));
  };

  return (
    <div className="min-h-screen flex bg-gray-50">
      {/* Sidebar */}
      <aside className="w-72 bg-white border-r border-gray-200 p-6 hidden md:flex flex-col gap-6">
        {/* Logo */}
        <motion.div 
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="flex items-center gap-3"
        >
          <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-indigo-600 to-purple-600 flex items-center justify-center text-white font-bold shadow-lg shadow-indigo-500/30">
            U
          </div>
          <div>
            <h1 className="text-lg font-bold bg-gradient-to-r from-indigo-600 to-purple-600 bg-clip-text text-transparent">
              UNIQUE ELEVATOR
            </h1>
            <p className="text-xs text-gray-400">Admin Dashboard</p>
          </div>
        </motion.div>

        {/* Navigation */}
        <nav className="flex flex-col gap-1 flex-1">
          <h4 className="text-xs text-gray-400 uppercase font-semibold mb-2 px-4">Main Menu</h4>
          {tabs.slice(0, 4).map((t) => (
            <SidebarItem
              key={t.name}
              icon={t.icon}
              label={t.name}
              badge={t.badge}
              active={activeTab === t.name}
              onClick={() => {
                setActiveTab(t.name);
                if (t.panel) openPanelHandler(t.panel);
              }}
            />
          ))}

          <div className="h-px bg-gray-200 my-4" />

          <h4 className="text-xs text-gray-400 uppercase font-semibold mb-2 px-4">Management</h4>
          {tabs.slice(4).map((t) => (
            <SidebarItem
              key={t.name}
              icon={t.icon}
              label={t.name}
              active={activeTab === t.name}
              onClick={() => {
                setActiveTab(t.name);
                if (t.panel) openPanelHandler(t.panel);
              }}
            />
          ))}
        </nav>

        {/* User Profile */}
        <motion.div
          whileHover={{ scale: 1.02 }}
          className="bg-gradient-to-r from-indigo-50 to-purple-50 rounded-2xl p-4 border border-indigo-100"
        >
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-gradient-to-br from-indigo-500 to-purple-500 flex items-center justify-center text-white font-semibold">
              A
            </div>
            <div className="flex-1">
              <p className="text-sm font-semibold text-gray-800">Admin</p>
              <p className="text-xs text-gray-500">Super Admin</p>
            </div>
            <button className="p-1.5 hover:bg-white rounded-lg transition-colors">
              <Settings size={16} className="text-gray-400" />
            </button>
          </div>
        </motion.div>

        <div className="text-xs text-gray-400 text-center">© 2025 Unique Elevator</div>
      </aside>

      {/* Main Content */}
      <div className="flex-1 flex flex-col">
        {/* Topbar */}
        <header className="sticky top-0 bg-white/80 backdrop-blur-lg z-30 border-b border-gray-200">
          <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between gap-4">
            {/* Search */}
            <div className="flex items-center gap-4 flex-1">
              <div className="hidden md:flex items-center gap-3 px-4 py-2.5 bg-gray-100 rounded-xl w-96">
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" className="text-gray-400">
                  <circle cx="11" cy="11" r="8" stroke="currentColor" strokeWidth="2" />
                  <path d="M21 21l-4.35-4.35" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                </svg>
                <input 
                  placeholder="Search anything..." 
                  className="bg-transparent outline-none text-sm flex-1"
                />
                <span className="text-xs text-gray-400">⌘K</span>
              </div>
            </div>

            {/* Actions */}
            <div className="flex items-center gap-3">
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                className="flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-indigo-600 to-purple-600 text-white rounded-xl shadow-lg shadow-indigo-500/30"
              >
                <PlusCircle size={18} />
                <span className="text-sm font-medium">Quick Add</span>
              </motion.button>

              {/* Notifications */}
              <div className="relative">
                <motion.button
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  onClick={() => setShowNotifications(!showNotifications)}
                  className="relative p-2.5 bg-gray-100 rounded-xl hover:bg-gray-200 transition-colors"
                >
                  <Bell size={20} className="text-gray-600" />
                  {notifications.length > 0 && (
                    <motion.span
                      initial={{ scale: 0 }}
                      animate={{ scale: 1 }}
                      className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 text-white text-xs rounded-full flex items-center justify-center"
                    >
                      {notifications.length}
                    </motion.span>
                  )}
                </motion.button>

                <AnimatePresence>
                  {showNotifications && (
                    <motion.div
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: 10 }}
                      className="absolute right-0 mt-2 w-96 bg-white rounded-2xl shadow-2xl border border-gray-200 overflow-hidden z-50"
                    >
                      <div className="p-4 border-b border-gray-100 flex items-center justify-between">
                        <h3 className="font-semibold text-gray-800">Notifications</h3>
                        <button className="text-xs text-indigo-600 hover:text-indigo-700">Mark all read</button>
                      </div>
                      <div className="max-h-96 overflow-y-auto">
                        {notifications.length === 0 ? (
                          <div className="p-8 text-center text-gray-400">
                            <Bell size={40} className="mx-auto mb-2 opacity-50" />
                            <p>No notifications yet</p>
                          </div>
                        ) : (
                          <AnimatePresence>
                            {notifications.map((n) => (
                              <NotificationItem 
                                key={n.id} 
                                notification={n} 
                                onDismiss={dismissNotification}
                              />
                            ))}
                          </AnimatePresence>
                        )}
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>

              {/* Login/Logout */}
              <div className="pl-3 border-l border-gray-200">
                {localStorage.getItem("userToken") ? (
                  <motion.button
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    onClick={() => {
                      // Clear admin-specific local storage
                      localStorage.removeItem("userToken");
                      localStorage.removeItem("userInfo");
                      
                      // Clear all cookies
                      const cookies = document.cookie.split(";");
                      cookies.forEach((cookie) => {
                        const eqPos = cookie.indexOf("=");
                        const name = eqPos > -1 ? cookie.substr(0, eqPos) : cookie;
                        document.cookie = name + "=;expires=Thu, 01 Jan 1970 00:00:00 GMT;path=/";
                        document.cookie = name + "=;expires=Thu, 01 Jan 1970 00:00:00 GMT;domain=" + window.location.hostname + ";path=/";
                      });

                      // Clear session storage
                      sessionStorage.clear();

                      // Clear Redux state
                      dispatch(clearOrders());
                      dispatch(clearCart());

                      navigate("/admin_login");
                    }}
                    className="px-4 py-2 bg-red-500 text-white rounded-xl hover:bg-red-600 transition text-sm font-medium"
                  >
                    Logout
                  </motion.button>
                ) : (
                  <motion.button
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    onClick={() => navigate("/admin_login")}
                    className="px-4 py-2 bg-green-500 text-white rounded-xl hover:bg-green-600 transition text-sm font-medium"
                  >
                    Login
                  </motion.button>
                )}
              </div>
            </div>
          </div>
        </header>

        {/* Dashboard Content */}
        <main className="flex-1 p-6 overflow-auto">
          <div className="max-w-7xl mx-auto">
            {/* Welcome Section */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="bg-gradient-to-r from-indigo-600 via-purple-600 to-pink-500 rounded-3xl p-8 mb-8 relative overflow-hidden"
            >
              <div className="absolute top-0 right-0 w-64 h-64 bg-white/10 rounded-full -translate-y-1/2 translate-x-1/2" />
              <div className="absolute bottom-0 left-0 w-48 h-48 bg-white/10 rounded-full translate-y-1/2 -translate-x-1/2" />
              
              <div className="relative flex items-center justify-between">
                <div className="flex items-center gap-6">
                  <div className="w-20 h-20 rounded-2xl bg-white/20 backdrop-blur flex items-center justify-center">
                    <Sparkles size={40} className="text-white" />
                  </div>
                  <div>
                    <h2 className="text-3xl font-bold text-white">Welcome Back!</h2>
                    <p className="text-white/80 mt-1">Here is what is happening with your dashboard today.</p>
                  </div>
                </div>
                
                <div className="hidden lg:flex items-center gap-8">
                  <div className="text-center">
                    <p className="text-4xl font-bold text-white">₹{dashboardStats.revenue.toLocaleString()}</p>
                    <p className="text-white/70 text-sm">Total Revenue</p>
                  </div>
                  <div className="text-center">
                    <p className="text-4xl font-bold text-white">{dashboardStats.orders}</p>
                    <p className="text-white/70 text-sm">Total Orders</p>
                  </div>
                  <div className="text-center">
                    <p className="text-4xl font-bold text-white">{dashboardStats.users}</p>
                    <p className="text-white/70 text-sm">Total Users</p>
                  </div>
                </div>
              </div>
            </motion.div>

            {/* Stats Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
              <StatCard
                title="Total Revenue"
                value={`₹${dashboardStats.revenue.toLocaleString()}`}
                change={12.5}
                icon={DollarSign}
                gradient="from-green-500 to-emerald-500"
                chartColor="#10b981"
              />
              <StatCard
                title="Total Orders"
                value={dashboardStats.orders}
                change={8.2}
                icon={ShoppingCart}
                gradient="from-blue-500 to-cyan-500"
                chartColor="#3b82f6"
              />
              <StatCard
                title="Active Users"
                value={dashboardStats.active}
                change={5.8}
                icon={Users}
                gradient="from-purple-500 to-pink-500"
                chartColor="#8b5cf6"
              />
              <StatCard
                title="Pending Orders"
                value={dashboardStats.pendingOrders}
                change={-2.4}
                icon={Clock}
                gradient="from-orange-500 to-amber-500"
                chartColor="#f59e0b"
              />
            </div>

            {/* Charts Section */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-8">
              {/* Revenue Chart */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.2 }}
                className="lg:col-span-2 bg-white rounded-2xl p-6 shadow-lg border border-gray-100"
              >
                <div className="flex items-center justify-between mb-6">
                  <div>
                    <h3 className="text-lg font-bold text-gray-800">Revenue Overview</h3>
                    <p className="text-sm text-gray-500">Monthly revenue comparison</p>
                  </div>
                  <div className="flex items-center gap-4">
                    <div className="flex items-center gap-2">
                      <span className="w-3 h-3 rounded-full bg-indigo-500" />
                      <span className="text-sm text-gray-600">2024</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="w-3 h-3 rounded-full bg-pink-500" />
                      <span className="text-sm text-gray-600">2023</span>
                    </div>
                  </div>
                </div>
                <div className="h-80">
                  <Line data={revenueLineData} options={revenueOptions} />
                </div>
              </motion.div>

              {/* Order Status */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.3 }}
                className="bg-white rounded-2xl p-6 shadow-lg border border-gray-100"
              >
                <h3 className="text-lg font-bold text-gray-800 mb-6">Order Status</h3>
                <div className="h-64 flex items-center justify-center">
                  <Doughnut data={orderStatusData} options={doughnutOptions} />
                </div>
                <div className="mt-4 flex items-center justify-center gap-6">
                  <div className="text-center">
                    <p className="text-2xl font-bold text-gray-800">{dashboardStats.completedOrders}</p>
                    <p className="text-xs text-gray-500">Completed</p>
                  </div>
                  <div className="text-center">
                    <p className="text-2xl font-bold text-gray-800">{dashboardStats.pendingOrders}</p>
                    <p className="text-xs text-gray-500">Pending</p>
                  </div>
                </div>
              </motion.div>
            </div>

            {/* Bottom Section */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              {/* Activity Feed */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.4 }}
                className="lg:col-span-2 bg-white rounded-2xl p-6 shadow-lg border border-gray-100"
              >
                <div className="flex items-center justify-between mb-6">
                  <h3 className="text-lg font-bold text-gray-800">Recent Activity</h3>
                  <button className="text-sm text-indigo-600 hover:text-indigo-700 flex items-center gap-1">
                    View All <ArrowRight size={16} />
                  </button>
                </div>
                <div className="space-y-4">
                  {[
                    { icon: CheckCircle, color: "bg-green-100 text-green-600", title: "Order Completed", desc: "Order #1234 has been delivered", time: "2 hours ago" },
                    { icon: AlertCircle, color: "bg-yellow-100 text-yellow-600", title: "Pending Payment", desc: "Payment for order #1233 pending", time: "3 hours ago" },
                    { icon: Users, color: "bg-blue-100 text-blue-600", title: "New User", desc: "John Doe registered as customer", time: "5 hours ago" },
                    { icon: Package, color: "bg-purple-100 text-purple-600", title: "Stock Update", desc: "Product stock updated for SKU-123", time: "Yesterday" },
                  ].map((activity, i) => (
                    <div key={i} className="flex items-center gap-4 p-4 bg-gray-50 rounded-xl hover:bg-gray-100 transition-colors">
                      <div className={`w-10 h-10 rounded-full flex items-center justify-center ${activity.color}`}>
                        <activity.icon size={18} />
                      </div>
                      <div className="flex-1">
                        <p className="text-sm font-medium text-gray-800">{activity.title}</p>
                        <p className="text-xs text-gray-500">{activity.desc}</p>
                      </div>
                      <span className="text-xs text-gray-400">{activity.time}</span>
                    </div>
                  ))}
                </div>
              </motion.div>

              {/* Quick Overview */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.5 }}
                className="bg-gradient-to-br from-indigo-600 to-purple-600 rounded-2xl p-6 text-white shadow-lg"
              >
                <h3 className="text-lg font-bold mb-6">Quick Overview</h3>
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 bg-white/20 rounded-lg flex items-center justify-center">
                        <Package size={16} />
                      </div>
                      <span className="text-sm">Total Products</span>
                    </div>
                    <span className="font-bold">{dashboardStats.totalProducts}</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 bg-white/20 rounded-lg flex items-center justify-center">
                        <CheckCircle size={16} />
                      </div>
                      <span className="text-sm">Completed</span>
                    </div>
                    <span className="font-bold">{dashboardStats.completedOrders}</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 bg-white/20 rounded-lg flex items-center justify-center">
                        <Clock size={16} />
                      </div>
                      <span className="text-sm">Pending</span>
                    </div>
                    <span className="font-bold">{dashboardStats.pendingOrders}</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 bg-white/20 rounded-lg flex items-center justify-center">
                        <Users size={16} />
                      </div>
                      <span className="text-sm">Active Users</span>
                    </div>
                    <span className="font-bold">{dashboardStats.active}</span>
                  </div>
                </div>
                
                <motion.button
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  className="w-full mt-6 py-3 bg-white/20 rounded-xl text-sm font-medium hover:bg-white/30 transition flex items-center justify-center gap-2"
                >
                  View Full Report <ArrowRight size={16} />
                </motion.button>
              </motion.div>
            </div>
          </div>
        </main>
      </div>

      {/* Centered Panel Modal */}
      <AnimatePresence>
        {openPanel && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/40 backdrop-blur-sm flex items-center justify-center z-50 p-4"
            onClick={closePanel}
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              className="bg-white w-full max-w-6xl max-h-[90vh] overflow-y-auto rounded-2xl shadow-2xl"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="sticky top-0 bg-white border-b border-gray-200 p-4 flex items-center justify-between z-10">
                <h2 className="text-xl font-bold text-gray-800 capitalize">{openPanel} Panel</h2>
                <button
                  onClick={closePanel}
                  className="p-2 hover:bg-gray-100 rounded-xl transition-colors"
                >
                  <X size={20} className="text-gray-500" />
                </button>
              </div>
              <div className="p-6">
                {openPanel === "users" && <UsersPanelContent usersSummary={dashboardStats} closePanel={closePanel} />}
                {openPanel === "orders" && <OrdersPanelContent />}
                {openPanel === "transactions" && <TransactionsPanelContent />}
                {openPanel === "messages" && <MessagesPanelContent />}
                {openPanel === "reviews" && <ReviewsPanelContent />}
                {openPanel === "settings" && <SettingsPanelContent />}
                {openPanel === "account" && <AccountPanelContent />}
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default AdminDashboard;
